import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { api } from "./api";
import { ErrorBanner, Spinner } from "./components/common";
import CurateScreen from "./screens/CurateScreen";
import ExploreScreen from "./screens/ExploreScreen";
import HomeScreen from "./screens/HomeScreen";
import QcScreen from "./screens/QcScreen";
import RunFilesScreen from "./screens/RunFilesScreen";
import RunsScreen from "./screens/RunsScreen";
import UploadScreen from "./screens/UploadScreen";
import { day } from "./lib/format";
import { DOCS_URL } from "./lib/links";
import { RELEASE_NOTES_URL, versionLabel } from "./lib/version";

/**
 * The backend's version, fetched once per page load.
 *
 * The promise is held outside the component because `Header` is mounted by
 * every layout and remounted on navigation; asking a second time would be one
 * request per screen change for a number that cannot move while the tab is
 * open. A failure clears the memo, so a header mounted later tries again rather
 * than inheriting one bad moment for the life of the session.
 */
let infoRequest = null;

/* Inline, like the copilot's icons, so the header needs no icon dependency and
   no network. Decorative: the link carries its own text, which is its
   accessible name, so the glyph is hidden from assistive technology. An open
   book — two leaves and a spine — because the site behind this link is prose to
   read, not a screen to work in. */
const BookIcon = (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 6.5S10 4.8 6.5 4.8H3.5v12.7h3C10 17.5 12 19.2 12 19.2" />
    <path d="M12 6.5s2-1.7 5.5-1.7h3v12.7h-3c-3.5 0-5.5 1.7-5.5 1.7" />
    <path d="M12 6.5v12.7" />
  </svg>
);

function useProductVersion() {
  const [label, setLabel] = useState(null);

  useEffect(() => {
    if (!infoRequest) {
      infoRequest = api.getInfo().catch((error) => {
        infoRequest = null;
        throw error;
      });
    }
    let live = true;
    infoRequest.then(
      (info) => live && setLabel(versionLabel(info)),
      // Deliberately silent. A missing version is not something to interrupt a
      // scientist about, and every screen that matters reports its own errors.
      () => {}
    );
    return () => {
      live = false;
    };
  }, []);

  return label;
}

function Header({ run }) {
  const version = useProductVersion();

  return (
    <header className="app-header">
      <Link className="brand-link" to="/">
        <span className="brand">Evaluwhale</span>
      </Link>
      {/* The version of the *backend*, which is what computed any number on
          screen, and a link to what changed in it. Absent rather than guessed
          when the API has not answered. */}
      {version && (
        <a
          className="version-link"
          href={RELEASE_NOTES_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Release notes"
        >
          {version}
        </a>
      )}
      <span style={{ opacity: 0.75 }}>CYP3A4 inhibition</span>
      {/* The run and its date, not who ran it. The operator is part of the record
          and is on the run's own page; putting a name in the chrome of every
          screen reads as whose session this is, which is not what it means. */}
      {run && (
        <span className="run-label">
          {run.title || run.manifest.run_id.slice(0, 8)}
          {run.manifest.metadata.experiment_start &&
            ` · ${day(run.manifest.metadata.experiment_start)}`}
        </span>
      )}
      {/* A new tab, not a route: documentation is read *while* curating a run,
          and navigating away would cost the selection and the tab in progress.
          The arrow that used to say so is now the `title`, because an arrow is
          one of several things a small glyph in a header bar could mean and a
          book is not. */}
      <a
        className="docs-link"
        href={DOCS_URL}
        target="_blank"
        rel="noopener noreferrer"
        title="Documentation — opens in a new tab"
        style={run ? null : { marginLeft: "auto" }}
      >
        {BookIcon}
        Docs
      </a>
    </header>
  );
}

/**
 * The numbered workflow. Three steps, and the count is load-bearing: what is
 * numbered here is what produces a result that can be signed off and exported.
 * Exported so a test can hold it to that — see `ExploreScreen.test.js`.
 */
export function Steps({ runId, qcAcknowledged }) {
  return (
    <nav className="steps">
      {/* For a run that exists, step 1 shows the files it was analysed from. The
          blank upload form belongs to a new run, and only to a new run. */}
      <NavLink
        to={runId ? `/runs/${runId}/upload` : "/new"}
        className={({ isActive }) => (isActive ? "active" : "")}
      >
        <span className="step-number">1</span>Upload
      </NavLink>
      <NavLink
        to={runId ? `/runs/${runId}/qc` : "#"}
        className={({ isActive }) =>
          [isActive ? "active" : "", runId ? "" : "disabled"].join(" ").trim()
        }
      >
        <span className="step-number">2</span>QC
      </NavLink>
      <NavLink
        to={runId ? `/runs/${runId}/curate` : "#"}
        className={({ isActive }) =>
          [isActive ? "active" : "", runId && qcAcknowledged ? "" : "disabled"].join(" ").trim()
        }
      >
        <span className="step-number">3</span>Curate and export
      </NavLink>
    </nav>
  );
}

/**
 * Everything under `/runs/:runId` shares the run's metadata and the step nav.
 *
 * The QC step gates the ones after it: a scientist has to have looked at the
 * internal-standard traces before working on the curves.
 */
function RunLayout() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);

  const refreshRun = useCallback(
    () =>
      api
        .getRun(runId)
        .then(setRun)
        .catch(setError),
    [runId]
  );

  useEffect(() => {
    refreshRun();
  }, [refreshRun]);

  if (error) {
    return (
      <>
        <Header />
        <main className="app-main">
          <ErrorBanner error={error} />
          <Link to="/runs">Back to the run list</Link>
        </main>
      </>
    );
  }

  return (
    <>
      <Header run={run} />
      <main className="app-main">
        <Steps runId={runId} qcAcknowledged={Boolean(run?.qc_acknowledged_at)} />
        {run ? <Outlet context={{ run, refreshRun }} /> : <Spinner label="Opening run…" />}
      </main>
    </>
  );
}

/** The screens that exist before there is a run to hang metadata off. */
function PlainLayout({ showSteps = false }) {
  return (
    <>
      <Header />
      <main className="app-main">
        {showSteps && <Steps runId={null} qcAcknowledged={false} />}
        <Outlet />
      </main>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PlainLayout />}>
          {/* The landing page says what this is; the run list is the work. */}
          <Route path="/" element={<HomeScreen />} />
          <Route path="/runs" element={<RunsScreen />} />
        </Route>
        <Route element={<PlainLayout showSteps />}>
          <Route path="/new" element={<UploadScreen />} />
        </Route>
        <Route path="/runs/:runId" element={<RunLayout />}>
          <Route index element={<Navigate to="qc" replace />} />
          <Route path="upload" element={<RunFilesScreen />} />
          <Route path="qc" element={<QcScreen />} />
          <Route path="curate" element={<CurateScreen />} />
          {/* Deliberately not a fourth entry in `Steps`. The numbered steps end
              in a result that can be signed off and exported; this one cannot
              produce anything reportable, and numbering it alongside them would
              say it could. Reached from a link on the curate screen. */}
          <Route path="explore" element={<ExploreScreen />} />
          {/* Export is a button on the curate screen now, not a step of its own. */}
          <Route path="export" element={<Navigate to="../curate" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
